import { Affix, Button, Card, Form, Space, Spin, message } from "antd";
import { MARINA_COVER_NOTE_BILL_LIST_ENDPOINT, MARINA_COVER_NOTE_BILL_PDF_ENDPOINT } from "apiServices/API_ENDPOINTS";
import { getData } from "apiServices/common";
import HandleNotification from "common/Notification";
import { isMobileView } from "components/Common/AppHelper";
import Header from "components/Common/Header";
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import MarinaCoverNoteBillForm from "../SubmitForm";
import { DownloadOutlined, PrinterOutlined } from "@ant-design/icons";

const UpdateMarinaCoverNoteBill = (props) => {
  const { width, viewMode } = props;
  const [editInfo, setEditInfo] = useState([]);
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const { marinaCoverNoteBillId } = useParams();
  const [loading, setLoading] = useState(true);

  const getMarinaCoverNoteBillInfo = useCallback(
    async (id) => {
      const query = `${MARINA_COVER_NOTE_BILL_LIST_ENDPOINT}/${id}`;
      const response = await getData(query);

      if (response && response?.data?.code === 200) {
        const marinaCoverNoteBillData = response?.data?.data;
        form.setFieldsValue({
          name: marinaCoverNoteBillData?.name,
          description: marinaCoverNoteBillData?.description,
        });
        setEditInfo(marinaCoverNoteBillData);
      } else {
        HandleNotification("error", "Error!", response?.message || "Failed to get Marine Cover Note Bill data");
      }
      setLoading(false);
    },
    [form],
  );

  useEffect(() => {
    if (marinaCoverNoteBillId) {
      getMarinaCoverNoteBillInfo(marinaCoverNoteBillId);
    }
  }, [marinaCoverNoteBillId, getMarinaCoverNoteBillInfo]);

  const discardForm = () => {
    navigate("/import-export-expense-tracking/marine-cover-note-bill");
  };

  const updateMarinaCoverNoteBill = () => {
    form.submit();
  };

  const getFileById = async (id) => {
    try {
      const query = `${MARINA_COVER_NOTE_BILL_PDF_ENDPOINT}/${id}`;

      const response = await getData(query, false, null, null, "arraybuffer");

      if (response && response.status === 200) {
        const blob = new Blob([response.data], { type: "application/pdf" });
        return URL.createObjectURL(blob);
      }
      return null;
    } catch (error) {
      console.error("Error fetching file:", error);
      return null;
    }
  };

  const handleDownload = () => {
    getFileById(marinaCoverNoteBillId)
      .then((blobUrl) => {
        if (blobUrl) {
          try {
            const link = document.createElement("a");
            link.href = blobUrl;
            link.download = `marine_cover_note.pdf`;
            link.target = "_blank";
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            setTimeout(() => URL.revokeObjectURL(blobUrl), 100);

            message.success({
              content: "Download started successfully",
              key: "download",
              duration: 2,
              marinaCoverNoteBillId,
            });
          } catch (error) {
            console.error("Download error:", error);
            message.error({
              content: "Error starting download",
              key: "download",
            });
          }
        } else {
          message.error({
            content: "No file available for download",
            key: "download",
          });
        }
      })
      .catch((error) => {
        console.error("Download error:", error);
        message.error({
          content: "Failed to download file",
          key: "download",
        });
      });
  };

  const handlePrint = () => {
    getFileById(marinaCoverNoteBillId)
      .then((blobUrl) => {
        if (blobUrl) {
          try {
            const iframe = document.createElement("iframe");
            iframe.style.position = "fixed";
            iframe.style.right = "0";
            iframe.style.bottom = "0";
            iframe.style.width = "0";
            iframe.style.height = "0";
            iframe.style.border = "0";
            iframe.src = blobUrl;

            document.body.appendChild(iframe);

            iframe.onload = () => {
              try {
                iframe.contentWindow.focus();

                setTimeout(() => {
                  iframe.contentWindow.print();

                  message.success({
                    content: "Print dialog opened",
                    key: "print",
                    duration: 3,
                  });
                }, 1000);
              } catch (printError) {
                console.error("Print dialog error:", printError);

                const printWindow = window.open(blobUrl, "_blank");
                if (printWindow) {
                  message.info({
                    content: "PDF opened in new tab. Please use browser print function.",
                    key: "print",
                    duration: 5,
                  });
                  setTimeout(() => URL.revokeObjectURL(blobUrl), 5000);
                } else {
                  message.error({
                    content: "Unable to open print dialog. Check popup settings.",
                    key: "print",
                  });
                  URL.revokeObjectURL(blobUrl);
                }

                document.body.removeChild(iframe);
              }
            };

            iframe.onerror = () => {
              document.body.removeChild(iframe);
              URL.revokeObjectURL(blobUrl);
              message.error({
                content: "Failed to load PDF for printing",
                key: "print",
              });
            };
          } catch (error) {
            console.error("Print error:", error);
            message.error({
              content: "Print functionality is not available",
              key: "print",
            });
            URL.revokeObjectURL(blobUrl);
          }
        } else {
          message.error({
            content: "No file available for printing",
            key: "print",
          });
        }
      })
      .catch((error) => {
        console.error("Print error:", error);
        message.error({
          content: "Failed to retrieve document for printing",
          key: "print",
        });
      });
  };

  const ActionUI = () => {
    return (
      <Space>
        {viewMode && (
          <>
            <Button
              loading={loading}
              disabled={loading}
              size="small"
              icon={<DownloadOutlined />}
              onClick={handleDownload}
              type="default"
            >
              Download
            </Button>
            <Button
              loading={loading}
              disabled={loading}
              size="small"
              icon={<PrinterOutlined />}
              onClick={handlePrint}
              type="default"
            >
              Print
            </Button>
          </>
        )}

        <Button size="small" key={1} danger onClick={() => discardForm()}>
          {viewMode ? "Back" : "Discard"}
        </Button>

        <Button size="small" key={2} type="primary" onClick={() => updateMarinaCoverNoteBill()} hidden={viewMode}>
          Update Bill
        </Button>
      </Space>
    );
  };

  return (
    <Spin Spin spinning={loading} tip="Loading...">
      <Affix offsetTop={0}>
        <Card className="p-fixed" style={{ width: `${isMobileView() ? "100%" : `${width}px`}` }}>
          <Header
            title={`${viewMode ? "View" : "Edit"} Marine Cover Note Bill`}
            subTitle=""
            addLink="/"
            addText="Add New"
            actions={<ActionUI />}
          />
        </Card>
      </Affix>

      <MarinaCoverNoteBillForm form={form} isEdit={true} viewMode={viewMode} editInfo={editInfo} />
    </Spin>
  );
};

export default UpdateMarinaCoverNoteBill;
